// «Управление» — the manager's screen (layout agreed with Tim 05.08).
// Four blocks top-to-bottom: all-pools summary tiles, the admins table with
// editable staff rates + live margin, unowned contributors with an assign
// control, and staff management (create / enable / money visibility).
//
// Only mounted for role=manager (main.tsx guards); every endpoint here is
// additionally manager-gated on the worker, so a stale tab degrades to 403s,
// not to data.
import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  api,
  type DatoricAdminStatsRow as AdminRow,
  type DatoricStaffMe as Me,
  type DatoricStaffListRes,
  type DatoricSummaryRes as Summary,
  type DatoricUnassignedRes,
} from '../api';
import { rateErrorText } from './PoolPage';

type StaffRow = DatoricStaffListRes['rows'][number];
type UnassignedRow = DatoricUnassignedRes['rows'][number];

const fmtH = (h: number): string => h.toFixed(h >= 100 ? 0 : 1);
// Sign before the currency: −$17.50, not $-17.50 (Tim, 06.08).
const fmtMoney = (cents: number | null): string =>
  cents == null ? '—' : `${cents < 0 ? '−' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;

const ROLE_LABEL: Record<StaffRow['role'], string> = {
  viewer: 'просмотр',
  admin: 'админ',
  manager: 'менеджер',
};

// No 0/O/1/l/I — these passwords get read aloud and retyped from messengers.
const PWD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
export const genPassword = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(14));
  return Array.from(bytes, (b) => PWD_ALPHABET[b % PWD_ALPHABET.length]).join('');
};

/** The message the manager forwards to a new hire — one copy button, done. */
const credsText = (email: string, password: string): string =>
  `datoric.weern.com\nлогин: ${email}\nпароль: ${password}`;

function CredsPanel({ email, password, onClose }: { email: string; password: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div class="creds">
      <pre>{credsText(email, password)}</pre>
      <div class="creds-actions">
        <button
          class="primary"
          onClick={() => {
            navigator.clipboard.writeText(credsText(email, password)).then(() => setCopied(true));
          }}
        >
          {copied ? '✓ Скопировано' : 'Скопировать логин-пароль'}
        </button>
        <button class="ghost" onClick={onClose}>Скрыть</button>
      </div>
      <div class="muted">Пароль показывается один раз — дальше только смена.</div>
    </div>
  );
}

export function ManagePage({ me }: { me: Me }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [admins, setAdmins] = useState<AdminRow[] | null>(null);
  const [unassigned, setUnassigned] = useState<UnassignedRow[] | null>(null);
  const [staff, setStaff] = useState<StaffRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    api.summary().then(setSummary).catch(() => {});
    api.adminStats().then((r) => setAdmins(r.rows)).catch(() => setAdmins([]));
    api.unassigned().then((r) => setUnassigned(r.rows)).catch(() => setUnassigned([]));
    api.staff().then((r) => setStaff(r.rows)).catch(() => setStaff([]));
  };
  useEffect(load, []);

  const fail = (e: unknown) => {
    setErr(rateErrorText(e) ?? String(e));
    load();
  };

  // Staff rate: blur/Enter commits, empty clears (which re-arms the cascade
  // gate for that admin — their contributor-rate edits start failing again).
  const saveStaffRate = async (row: AdminRow, raw: string) => {
    const dollars = raw.trim() === '' ? null : Number(raw);
    if (dollars !== null && (!Number.isFinite(dollars) || dollars < 0)) return;
    const rateCents = dollars === null ? null : Math.round(dollars * 100);
    if (rateCents === row.rateCents) return;
    try {
      await api.patchStaff(row.id, { rateCents });
      setErr(null);
      load(); // margin depends on the rate — recompute server-side, not locally
    } catch (e) {
      fail(e);
    }
  };

  const assignTo = async (contributorId: string, userId: string) => {
    if (!userId) return;
    try {
      await api.assign(contributorId, userId);
      setErr(null);
      load();
    } catch (e) {
      fail(e);
    }
  };

  const toggleStaff = async (row: StaffRow, patch: { disabled?: boolean; showBalance?: boolean }) => {
    try {
      await api.patchStaff(row.id, patch);
      setErr(null);
      load();
    } catch (e) {
      fail(e);
    }
  };

  // Password reset: the manager is the only reset path while email is out.
  const [pwdFor, setPwdFor] = useState<string | null>(null);
  const [pwdValue, setPwdValue] = useState('');
  // Freshly set credentials — rendered once with a copy button, never stored.
  const [creds, setCreds] = useState<{ email: string; password: string } | null>(null);
  const savePassword = async (row: StaffRow) => {
    if (pwdValue.length < 8) {
      setErr('Пароль — минимум 8 символов.');
      return;
    }
    try {
      await api.patchStaff(row.id, { password: pwdValue });
      setErr(null);
      setCreds({ email: row.email, password: pwdValue });
      setPwdFor(null);
      setPwdValue('');
    } catch (e) {
      fail(e);
    }
  };

  // Assign targets: anyone active. Admins own pools; a manager can also take
  // people directly (that is how «бесхозные» get triaged before hiring).
  const assignTargets = useMemo(
    () => (staff ?? []).filter((s) => s.disabled === 0 && s.role !== 'viewer'),
    [staff],
  );

  return (
    <>
      {summary && (
        <div class="tiles">
          <div class="tile"><div class="n">{fmtH(summary.acceptedHours)} ч</div><div class="t">принято</div></div>
          <div class="tile"><div class="n">{fmtH(summary.pendingHours)} ч</div><div class="t">на проверке</div></div>
          <div class="tile"><div class="n">{fmtH(summary.rejectedHours)} ч</div><div class="t">брак</div></div>
          <div class="tile"><div class="n">{fmtMoney(summary.payoutCents)}</div><div class="t">заработано</div></div>
          <div class="tile"><div class="n">{summary.matched}/{summary.people}</div><div class="t">активировались</div></div>
        </div>
      )}
      {err && <div class="strip bad">Не сохранилось: {err}</div>}

      <h2 class="section">Админы и пулы</h2>
      <div class="tablewrap">
        {admins == null ? (
          <div class="empty">Загружаем…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Роль</th>
                <th>Ставка $/ч</th>
                <th>Людей</th>
                <th>Принято ч</th>
                <th>На проверке</th>
                <th>Брак ч</th>
                <th>Контрибьюторам</th>
                <th>Маржа</th>
              </tr>
            </thead>
            <tbody>
              {admins
                .filter((a) => !a.disabled || a.poolSize > 0)
                .map((a) => (
                  <tr
                    class="row"
                    key={a.id}
                    onClick={() => (location.hash = `#/?owner=${encodeURIComponent(a.id)}`)}
                  >
                    <td>
                      {a.name}
                      {a.disabled && <span class="chip fraud">выключен</span>}
                    </td>
                    <td>{ROLE_LABEL[a.role]}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        class="rate-input"
                        type="number"
                        min="0"
                        step="0.5"
                        value={a.rateCents == null ? '' : (a.rateCents / 100).toString()}
                        placeholder="—"
                        onChange={(e) => saveStaffRate(a, (e.target as HTMLInputElement).value)}
                      />
                    </td>
                    <td>
                      {a.poolSize}
                      {a.unratedCount > 0 && <span class="sub">без ставки: {a.unratedCount}</span>}
                    </td>
                    <td>{fmtH(a.acceptedHours)}</td>
                    <td>{a.pendingHours > 0 ? <span class="chip pend">{fmtH(a.pendingHours)}</span> : '0'}</td>
                    <td>{fmtH(a.rejectedHours)}</td>
                    <td>{fmtMoney(a.contributorPayoutCents)}</td>
                    {/* «—» here means "no admin rate yet", not "zero margin".
                        Red = pre-validator data where a pool rate exceeds the
                        admin's — fix the rates, the number is honest. */}
                    <td class={a.marginCents != null && a.marginCents < 0 ? 'neg' : ''}>
                      {fmtMoney(a.marginCents)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <h2 class="section">Бесхозные {unassigned && unassigned.length > 0 ? `(${unassigned.length})` : ''}</h2>
      <div class="tablewrap">
        {unassigned == null ? (
          <div class="empty">Загружаем…</div>
        ) : unassigned.length === 0 ? (
          <div class="empty">Все контрибьюторы разобраны по пулам.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Статус</th>
                <th>Принято ч</th>
                <th>Назначить</th>
              </tr>
            </thead>
            <tbody>
              {unassigned.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.email}
                    {u.name && <span class="sub">{u.name}</span>}
                  </td>
                  <td><span class={`chip ${u.status}`}>{u.status}</span></td>
                  <td>{fmtH(u.accepted_seconds / 3600)}</td>
                  <td>
                    <select
                      value=""
                      onChange={(e) => assignTo(u.id, (e.target as HTMLSelectElement).value)}
                    >
                      <option value="">— выбрать админа —</option>
                      {assignTargets.map((s) => (
                        <option value={s.id} key={s.id}>
                          {s.name} ({ROLE_LABEL[s.role]})
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <h2 class="section">Сотрудники</h2>
      <div class="tablewrap">
        {staff == null ? (
          <div class="empty">Загружаем…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Имя</th>
                <th>Роль</th>
                <th>Пул</th>
                <th>Активен</th>
                <th>Видит деньги</th>
                <th>Пароль</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id}>
                  <td>{s.email}</td>
                  <td>{s.name}</td>
                  <td>{ROLE_LABEL[s.role]}</td>
                  <td>{s.pool_size}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={s.disabled === 0}
                      disabled={s.id === me.id}
                      onChange={(e) =>
                        toggleStaff(s, { disabled: !(e.target as HTMLInputElement).checked })
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={s.show_balance === 1}
                      onChange={(e) =>
                        toggleStaff(s, { showBalance: (e.target as HTMLInputElement).checked })
                      }
                    />
                  </td>
                  <td>
                    {pwdFor === s.id ? (
                      <span class="pwd-edit">
                        <input
                          type="text"
                          class="rate-input pwd-input"
                          placeholder="новый (мин. 8)"
                          value={pwdValue}
                          onInput={(e) => setPwdValue((e.target as HTMLInputElement).value)}
                          onKeyDown={(e) => {
                            if ((e as KeyboardEvent).key === 'Enter') savePassword(s);
                            if ((e as KeyboardEvent).key === 'Escape') setPwdFor(null);
                          }}
                        />
                        <button class="link" title="Сгенерировать" onClick={() => setPwdValue(genPassword())}>🎲</button>
                        <button class="link" onClick={() => savePassword(s)}>сохранить</button>
                      </span>
                    ) : (
                      <button
                        class="link"
                        onClick={() => {
                          setPwdFor(s.id);
                          setPwdValue('');
                        }}
                      >
                        сменить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {creds && <CredsPanel email={creds.email} password={creds.password} onClose={() => setCreds(null)} />}
      <CreateStaff onDone={load} onError={(m) => setErr(m)} onCreated={setCreds} />
    </>
  );
}

// ── create staff ────────────────────────────────────────────────────────────

function CreateStaff({
  onDone,
  onError,
  onCreated,
}: {
  onDone: () => void;
  onError: (m: string) => void;
  onCreated: (c: { email: string; password: string }) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'viewer' | 'admin' | 'manager'>('admin');
  const [busy, setBusy] = useState(false);

  const valid = email.trim() && name.trim() && password.length >= 8;

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const created = await api.createStaff({ email: email.trim(), name: name.trim(), role, password });
      onCreated({ email: created.email, password });
      setEmail('');
      setName('');
      setPassword('');
      onDone();
    } catch (err) {
      onError(String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form class="toolbar" onSubmit={submit}>
      <input
        type="email"
        placeholder="email нового сотрудника"
        value={email}
        onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
      />
      <input
        type="text"
        placeholder="имя"
        value={name}
        onInput={(e) => setName((e.target as HTMLInputElement).value)}
      />
      <input
        type="text"
        placeholder="пароль (мин. 8)"
        value={password}
        onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
      />
      <button type="button" class="ghost" title="Сгенерировать пароль" onClick={() => setPassword(genPassword())}>
        🎲
      </button>
      <select value={role} onChange={(e) => setRole((e.target as HTMLSelectElement).value as typeof role)}>
        <option value="admin">админ</option>
        <option value="viewer">просмотр</option>
        <option value="manager">менеджер</option>
      </select>
      <button class="primary" disabled={busy || !valid}>
        {busy ? 'Создаём…' : '+ Сотрудник'}
      </button>
    </form>
  );
}
